import { Button, Pill, Text } from "components/commons";
import styles from "../shift-records-details-cash-and-non-cash.module.scss";
import { formatAmount } from "utils";
import { PaymentMethod } from "enums";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import locale from "localization";
import Big from "big.js";
import { sortBy } from "lodash";

export const getKeyAccountPayments = (shift) => {
  const keyAccountRefunds = (shift?.transactionPaymentMethod?.refund || []).filter(
    (r) => r.method === PaymentMethod.KeyAccount,
  );
  return sortBy(
    (shift?.transactionPaymentMethod?.nonCash || [])
      .filter((nonCash) => nonCash.method === PaymentMethod.KeyAccount)
      .map((nonCash) => {
        const refund = keyAccountRefunds.find((r) => r.sku === nonCash.sku);
        if (!refund) return nonCash;
        const refundDiscount =
          refund.discount !== 0 && refund.vatAmount !== 0
            ? Big(refund.discount).add(refund.vatAmount).toNumber()
            : refund.discount;

        const quantity = Big(nonCash.quantity).add(refund.quantity).toNumber();
        const totalAmount = Big(nonCash.totalAmount).add(refund.totalAmount).toNumber();
        const discount = Big(nonCash.discount).minus(refundDiscount).toNumber();
        const vatAmount = Big(nonCash.vatAmount).add(refund.vatAmount).toNumber();
        const finalAmount = Big(totalAmount).add(discount).add(vatAmount).toNumber();

        return {
          ...nonCash,
          quantity,
          totalAmount,
          finalAmount,
          discount,
          vatAmount,
        };
      }),
    "transactionItemId",
  ).reverse();
};

export const getKeyAccountGroupKey = (payment) =>
  payment.keyAccountId || payment.keyAccountName || "";

export const getKeyAccountSales = (shift) =>
  getKeyAccountPayments(shift)
    .reduce((sum, t) => Big(sum).add(t.finalAmount || 0), Big(0))
    .round(2)
    .toNumber();

const summarize = (payments) => {
  const sum = (key) =>
    payments.reduce((acc, t) => Big(acc).add(t[key] || 0), Big(0)).toNumber();

  return {
    transactionCount: [...new Set(payments.map((t) => t.transactionId))].length,
    adjustmentCount: [
      ...new Set(payments.filter((t) => t.adjustedAt).map((t) => t.transactionId)),
    ].length,
    totalAmount: Big(sum("totalAmount")).round(2).toNumber(),
    discount: sum("discount"),
    vatAmount: sum("vatAmount"),
    excessAmount: sum("excessAmount"),
    finalAmount: sum("finalAmount"),
  };
};

export const groupKeyAccountPayments = (payments) => {
  const grouped = {};
  payments.forEach((payment) => {
    const key = getKeyAccountGroupKey(payment);
    grouped[key] = [...(grouped[key] || []), payment];
  });

  const rows = Object.entries(grouped)
    .map(([key, value]) => ({
      key,
      keyAccountName: value.find((t) => t.keyAccountName)?.keyAccountName || "",
      ...summarize(value),
    }))
    .sort((a, b) => a.keyAccountName.localeCompare(b.keyAccountName));

  if (!rows.length) return rows;

  return [...rows, { key: "total", isTotal: true, ...summarize(payments) }];
};

export const keyAccountSummaryMapper = ({ val, onViewKeyAccountTransaction }) => {
  const {
    key,
    isTotal,
    keyAccountName,
    transactionCount,
    adjustmentCount,
    totalAmount,
    discount,
    vatAmount,
    excessAmount,
    finalAmount,
  } = val;
  const contentClass = isTotal ? styles.label : styles.content;
  const vatVal = vatAmount ?? 0;

  return {
    keyAccountName: (
      <Text className={isTotal ? styles.label : styles.content}>
        {isTotal ? locale.totalKeyAccountSales : keyAccountName || "--"}
      </Text>
    ),
    transactionCount: (
      <div className={styles.transactionCountCell}>
        <Text className={contentClass}>{transactionCount}</Text>
        {!isTotal && adjustmentCount > 0 && (
          <Pill className={styles.adjustmentCountPill}>
            {locale.populate(locale.xAdjustments, [adjustmentCount])}
          </Pill>
        )}
      </div>
    ),
    totalAmount: <Text className={contentClass}>{formatAmount(totalAmount)}</Text>,
    discount: (
      <Text className={discount < 0 ? styles.negative : contentClass}>
        {`${discount < 0 ? "-" : ""}${formatAmount(discount)}`}
      </Text>
    ),
    vatExempt: (
      <Text className={vatVal < 0 ? styles.negative : contentClass}>
        {vatVal < 0 ? `-${formatAmount(-vatVal)}` : formatAmount(vatVal)}
      </Text>
    ),
    finalAmount: (
      <>
        <Text className={contentClass}>{formatAmount(finalAmount)}</Text>
        {!isNaN(excessAmount) && excessAmount >= 0.005 && (
          <Text className={styles.italic}>
            {"Excess:"}
            {formatAmount(excessAmount)}
          </Text>
        )}
      </>
    ),
    actions: isTotal ? null : (
      <Button
        style={{ textDecoration: "none" }}
        link
        onClick={() => onViewKeyAccountTransaction(key)}
      >
        <div
          style={{
            color: "#5349b7",
            fontWeight: "500",
            display: "flex",
            justifyContent: "center",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          <span>{locale.view}</span>
          <NavigateNextIcon fontSize="small" />
        </div>
      </Button>
    ),
  };
};
